import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export function PayoutAdjustmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), `/accounting/payouts?open=${id ?? ''}`);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      post(`/accounting/payouts/${id}/adjustments`, {
        amountMinor: minorFromInput(amount),
        reason: reason.trim(),
        reference: reference.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(tr('billing', 'adjustment_added'));
      void queryClient.invalidateQueries({ queryKey: ['payout-advice', id] });
      void queryClient.invalidateQueries({ queryKey: ['payouts'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = minorFromInput(amount) > 0 && reason.trim().length >= 5;
  const dirty = !save.isSuccess && (amount !== '' || reason !== '' || reference !== '');

  return (
    <FormScreen
      title={tr('billing', 'add_an_adjustment')}
      description={tr('billing', 'for_money_owed_that_no_bill_line_shows_such_as')}
      eyebrow={tr('billing', 'doctor_payouts')}
      icon={SlidersHorizontal}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'adjustment')} icon={SlidersHorizontal}>
        <Field label={tr('billing', 'amount')} required>
          <Input dir="ltr" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
        </Field>
        <Field label={tr('billing', 'contract_reference')}>
          <Input value={reference} onChange={(event) => setReference(event.target.value)} />
        </Field>
        <Field label={tr('billing', 'reason')} required className="sm:col-span-2">
          <Input value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'add')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
